// Reusable in-stage activity widgets. Each returns { id, mount(el, rt, api), verify(data, rt) }.
// api: { done(), state:{done,data}, save(), refresh() }
// verify(data, rt) is the same completion rule without the DOM: the server (src/core/labVerify.js)
// calls it with the data the learner submitted, against the replayed lab at that moment.

import { h } from './h.js';

/**
 * PREDICT: the learner commits a prediction before testing it. Any answer is
 * accepted — the point is to state an expectation, then compare with evidence.
 */
export function predict({ id, title = 'PREDICT BEFORE YOU TEST', prompt, options, freeText = false, min = 12 }) {
  return {
    id, blocker: 'Record your prediction',
    verify: (d) => (freeText ? typeof d?.text === 'string' && d.text.trim().length >= min : (options || []).includes(d?.choice)),
    mount(el, rt, api) {
      const s = api.state.data;
      const render = () => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, title, s.choice || s.text ? h('span', { class: 'ok' }, '✓ RECORDED') : null), h('p', {}, prompt));
        if (s.choice || s.text) {
          el.append(h('div', { class: 'pred-locked', 'data-testid': `pred-${id}` }, h('b', {}, 'Your prediction: '), s.choice || s.text), h('div', { class: 'muted small' }, 'Now gather evidence in the lab and compare it with what you expected.'));
          return;
        }
        if (freeText) {
          const ta = h('textarea', { placeholder: 'Write what you expect to happen and why…', 'data-testid': `pred-text-${id}` });
          const err = h('div', { class: 'form-err' });
          el.append(ta, err, h('div', { class: 'row' }, h('button', { class: 'btn small', 'data-testid': `pred-save-${id}`, onclick: () => {
            if (ta.value.trim().length < min) { err.textContent = `Write at least ${min} characters — a real expectation, not a placeholder.`; return; }
            s.text = ta.value.trim(); rt.action('predict', { widget: id, text: s.text }); api.done(); render();
          } }, 'Record prediction')));
        } else {
          el.append(h('div', { class: 'opt-row' }, options.map((o) => h('button', { class: 'opt', 'data-testid': `pred-opt`, onclick: () => { s.choice = o; rt.action('predict', { widget: id, choice: o }); api.done(); render(); } }, o))));
        }
      };
      render();
    },
  };
}

/**
 * DISPATCH / SORT: assign each item to a category, with explanations. Must be
 * fully correct to complete — mistakes show the reasoning, not the answer.
 */
export function sorter({ id, title, intro, categories, items }) {
  return {
    id, blocker: `Complete: ${title}`,
    verify: (d) => items.every((it, i) => d?.pick?.[i] === it.answer),
    mount(el, rt, api) {
      const s = api.state.data;
      s.pick = s.pick || {};
      const render = (checked) => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, title, api.state.done ? h('span', { class: 'ok' }, '✓ COMPLETE') : null));
        if (intro) el.append(h('p', { class: 'small muted' }, intro));
        const grid = h('div', { class: 'w-grid' });
        items.forEach((it, i) => {
          const val = s.pick[i] || '';
          const good = checked && val === it.answer;
          const bad = checked && val && val !== it.answer;
          const sel = h('select', { 'data-testid': `sort-${id}-${i}`, disabled: api.state.done || null, onchange: (e) => { s.pick[i] = e.target.value; api.save(); } },
            h('option', { value: '' }, 'Assign…'), categories.map((c) => h('option', { value: c, selected: val === c || null }, c)));
          grid.append(h('div', { class: `w-row ${good || api.state.done ? 'good' : ''} ${bad ? 'bad' : ''}` },
            h('div', { html: it.text }), sel,
            (bad && it.why) ? h('div', { class: 'w-fb bad' }, it.why) : null,
            ((good || api.state.done) && it.explain) ? h('div', { class: 'w-fb good' }, it.explain) : null));
        });
        el.append(grid);
        if (!api.state.done) {
          el.append(h('div', { class: 'row' }, h('button', { class: 'btn small', 'data-testid': `sort-check-${id}`, onclick: () => {
            const all = items.every((it, i) => s.pick[i] === it.answer);
            rt.action('sort-check', { widget: id, ok: all });
            if (all) api.done();
            render(true);
          } }, 'Check assignments')));
        }
      };
      render(api.state.done);
    },
  };
}

/**
 * RECORD OBSERVATIONS: the learner reads values off real command output and
 * writes them down. Each field is validated against live lab state, so the
 * only way to fill it in is to go and look.
 */
export function observe({ id, title = 'RECORD YOUR OBSERVATIONS', intro, fields }) {
  return {
    id, blocker: `Record the observations in “${title.toLowerCase()}”`,
    verify: (d, rt) => fields.every((f, i) => { try { return f.check(String(d?.v?.[i] ?? '').trim(), rt) === true; } catch { return false; } }),
    mount(el, rt, api) {
      const s = api.state.data;
      s.v = s.v || {};
      const render = (msgs = {}) => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, title, api.state.done ? h('span', { class: 'ok' }, '✓ VERIFIED') : null));
        if (intro) el.append(h('p', { class: 'small muted' }, intro));
        const g = h('div', { class: 'w-grid' });
        fields.forEach((f, i) => {
          g.append(h('label', { class: 'fld' }, h('span', {}, f.label),
            h('input', { class: 'mono', value: s.v[i] || '', placeholder: f.placeholder || '', disabled: api.state.done || null, 'data-testid': `obs-${id}-${i}`, oninput: (e) => { s.v[i] = e.target.value; } }),
            msgs[i] ? h('span', { class: `w-fb ${msgs[i].ok ? 'good' : 'bad'}` }, msgs[i].text) : null));
        });
        el.append(g);
        if (!api.state.done) el.append(h('div', { class: 'row' }, h('button', { class: 'btn small', 'data-testid': `obs-check-${id}`, onclick: () => {
          const m = {};
          let all = true;
          fields.forEach((f, i) => {
            const r = f.check((s.v[i] || '').trim(), rt);
            const ok = r === true;
            if (!ok) all = false;
            m[i] = { ok, text: ok ? (f.ok || 'Matches the device.') : (typeof r === 'string' ? r : 'That does not match what the device reports. Look again.') };
          });
          rt.action('observe', { widget: id, ok: all });
          if (all) api.done();
          render(m);
        } }, 'Check against the device')));
      };
      render();
    },
  };
}

/** Free-text engineering note (expected behaviour, plan, reflection). */
export function note({ id, title, prompt, min = 30, placeholder }) {
  return {
    id, blocker: `Write: ${title.toLowerCase()}`,
    verify: (d) => typeof d?.text === 'string' && d.text.trim().length >= min,
    mount(el, rt, api) {
      const s = api.state.data;
      const render = () => {
        el.textContent = '';
        el.append(h('div', { class: 'w-title' }, title, api.state.done ? h('span', { class: 'ok' }, '✓ SAVED') : null), h('p', { class: 'small' }, prompt));
        if (api.state.done) { el.append(h('div', { class: 'pred-locked' }, s.text)); return; }
        const ta = h('textarea', { placeholder: placeholder || '', 'data-testid': `note-${id}` }, s.text || '');
        const err = h('div', { class: 'form-err' });
        el.append(ta, err, h('div', { class: 'row' }, h('button', { class: 'btn small', 'data-testid': `note-save-${id}`, onclick: () => {
          if (ta.value.trim().length < min) { err.textContent = `Write at least ${min} characters.`; return; }
          s.text = ta.value.trim(); rt.action('note', { widget: id }); api.done(); render();
        } }, 'Save note')));
      };
      render();
    },
  };
}

/** Plain acknowledgement for a reading section inside a lesson. */
export function readConfirm({ id, label = 'I have read this section and I am ready to apply it' }) {
  return {
    id, blocker: 'Confirm you finished the reading',
    verify: () => true,
    mount(el, rt, api) {
      el.classList.add('slim');
      const render = () => {
        el.textContent = '';
        el.append(api.state.done ? h('div', { class: 'ok-text small' }, '✓ Reading complete')
          : h('button', { class: 'btn small', 'data-testid': `read-${id}`, onclick: () => { rt.action('read', { widget: id }); api.done(); render(); } }, label));
      };
      render();
    },
  };
}
